/**
 * The daily famous game, on the board.
 *
 * `set.js` decides the puzzle and how a try is marked; this page plays it. It is
 * the guess page's board (`play/table.js`) with a different host: the visitor
 * plays BOTH sides — the next five half-moves of the game — so the side allowed
 * to pick a man up is whoever is to move, and every move is played onto the
 * board, because a try is a line and a line has to be seen to be judged.
 *
 * After a try is marked the board goes back to the puzzle's position for the
 * next one. After the last try, or the right one, the real five moves are played
 * out, and then comes the one question the board cannot answer: whose game?
 *
 * WHAT IS KEPT, under `immortal-daily` (and on the account, see `progress.js`):
 * `{ [day]: { done, won, n, who, tries } }` — the marks for each try, so a reload
 * mid-puzzle carries on and a finished one still has its grid to share. Only
 * today's marks are kept; older days keep the outcome, which is all a streak
 * needs, so the value stays small however long somebody plays.
 */

import { createTable, escapeHtml, uciOf } from '../play/table.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { publicUrl } from '../public-url.js';
import { onMerged, read as readProgress, syncSoon, write as writeProgress } from '../progress.js';
import {
  LENGTH, TRIES, dayNumber, puzzleFor, scoreTry, shareText, solved, streakOf,
} from './set.js';

const $ = (id) => document.getElementById(id);

mountNav($('nav-slot'), { here: 'daily', base: '../' });
mountInstall($('install-slot'), { base: '../' });

const KEY = 'immortal-daily';
/** How many days of outcomes are kept. A year and a bit: any streak worth having. */
const KEEP_DAYS = 400;

const today = dayNumber();
const puzzle = puzzleFor(today);

function readResults() {
  try {
    const raw = JSON.parse(readProgress(KEY) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch { return {}; }
}

let results = readResults();

/** Today's entry, as the page works on it. */
function blank() {
  return { done: false, won: false, n: 0, who: null, tries: [] };
}
let record = { ...blank(), ...(results[today] || {}) };
record.tries = Array.isArray(record.tries) ? record.tries : [];

function save() {
  results[today] = { ...record };
  for (const day of Object.keys(results)) {
    const d = Number(day);
    if (!Number.isFinite(d) || d < today - KEEP_DAYS) delete results[day];
    else if (d !== today) delete results[day].tries;
  }
  writeProgress(KEY, JSON.stringify(results));
}

onMerged(() => {
  results = readResults();
  const theirs = results[today];
  // Another device got further with today's puzzle: take its state and show it.
  if (theirs?.done && !record.done) {
    record = { ...blank(), ...theirs, tries: theirs.tries || record.tries };
    finish();
  }
  drawStreak();
});
syncSoon();

const table = await createTable();
// The six angles as one button over the board (`../camera-menu.js`).
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

/** guessing | reveal | who | over */
let phase = 'guessing';
let busy = false;
/** This try so far: the moves in SAN, and in UCI to rebuild the board. */
let current = [];
let currentUci = [];

const sideWord = (side) => (side === 'w' ? 'White' : 'Black');

// ------------------------------------------------------------------ drawing

function drawGrid() {
  const rows = [];
  for (let t = 0; t < TRIES; t++) {
    const marks = record.tries[t];
    const open = !marks && t === record.tries.length && phase === 'guessing';
    const cells = [];
    for (let i = 0; i < LENGTH; i++) {
      const mark = marks ? marks[i] : '';
      const text = open ? current[i] || '' : '';
      cells.push(`<span class="d-cell ${mark}${open && i === current.length ? ' next' : ''}" role="cell">${escapeHtml(text)}</span>`);
    }
    rows.push(`<div class="d-row${open ? ' live' : ''}" role="row">${cells.join('')}</div>`);
  }
  $('grid').innerHTML = rows.join('');
}

function drawStreak() {
  const streak = streakOf(results, today);
  $('streak').textContent = streak > 1 ? `🔥 ${streak} days` : `Daily #${today}`;
}

/** The panel's pills. They carry the streak too, because on a phone the
 *  masthead has no room for its chip (`daily.css`). */
function drawPills() {
  const streak = streakOf(results, today);
  table.setPills([
    streak > 1 ? `Daily #${today} · 🔥 ${streak} days` : `Daily #${today}`,
    `${sideWord(puzzle.side)} to move`,
  ]);
}

function drawAsk() {
  $('ask').innerHTML = `<b>Move ${puzzle.moveNo}, ${sideWord(puzzle.side)} to play.</b>`
    + ' Play the next five moves of this famous game — both sides.';
  drawPills();
}

function lead() {
  if (phase !== 'guessing') return;
  const turn = sideWord(live.pos.turn);
  table.setLead(`Try ${record.tries.length + 1} of ${TRIES} · move ${current.length + 1} of ${LENGTH} · ${turn} to play`);
  table.setTag(`${TRIES - record.tries.length} left`);
}

// ------------------------------------------------------------------ playing

async function toStart() {
  current = [];
  currentUci = [];
  await table.setScore(puzzle.before);
  table.you = live.pos.turn;
}

table.canPick = () => phase === 'guessing' && !busy;
table.onPick = async (mv) => {
  busy = true;
  try {
    const legal = live.pos.generate();
    const san = live.pos.san(mv, legal);
    await table.apply(mv);
    current.push(san);
    currentUci = live.uci().slice(puzzle.before.length);
    table.you = live.pos.turn;
    drawGrid();
    // Five moves, or a line that ended the game early: either way, mark it.
    if (current.length === LENGTH || live.pos.generate().length === 0) await submit();
    else lead();
  } finally {
    busy = false;
  }
};

async function submit() {
  const marks = scoreTry(current, puzzle.answer);
  record.tries.push(marks);
  record.n = record.tries.length;
  save();
  drawGrid();
  if (solved(marks)) return reveal(true);
  if (record.tries.length >= TRIES) return reveal(false);
  const hits = marks.filter((m) => m === 'hit').length;
  table.say(hits ? `${hits} of ${LENGTH} right. Again from the start.` : 'None of those. Again from the start.');
  await new Promise((r) => { setTimeout(r, 900); });
  await toStart();
  lead();
}

async function undo() {
  if (phase !== 'guessing' || busy || !current.length) return;
  busy = true;
  try {
    current.pop();
    currentUci.pop();
    await table.setScore([...puzzle.before, ...currentUci]);
    table.you = live.pos.turn;
    drawGrid();
    lead();
  } finally {
    busy = false;
  }
}

/** The real moves, played out, then the question. */
async function reveal(won) {
  phase = 'reveal';
  record.won = won;
  // Kept, so a reload after giving up comes back to the answer rather than to
  // the guesses, with the answer now known.
  record.revealed = true;
  save();
  $('undo').disabled = true;
  $('give-up').disabled = true;
  drawGrid();
  table.setLead(won ? 'That is what was played.' : 'This is what was played.');
  table.setTag('');
  await table.setScore(puzzle.before);
  for (const uci of puzzle.answerUci) await table.applyUci(uci);
  table.showMoves();
  phase = 'who';
  drawChoices();
}

function drawChoices() {
  $('who').hidden = false;
  $('choices').innerHTML = puzzle.choices
    .map((label, i) => `<button class="ctl" type="button" data-at="${i}">${escapeHtml(label)}</button>`)
    .join('');
}

function choose(label) {
  if (phase !== 'who') return;
  record.who = label === puzzle.label;
  record.done = true;
  save();
  for (const button of $('choices').children) {
    const text = button.textContent;
    button.disabled = true;
    button.classList.toggle('right', text === puzzle.label);
    button.classList.toggle('wrong', text === label && text !== puzzle.label);
  }
  count('daily-done', `d${today}`, streakOf(results, today));
  finish();
}

/** The end: what it was, how it went, and what to do with it. */
async function finish() {
  phase = 'over';
  $('undo').disabled = true;
  $('give-up').disabled = true;
  if (live.ply !== puzzle.before.length + LENGTH) {
    await table.setScore([...puzzle.before, ...puzzle.answerUci]);
    table.showMoves();
  }
  if ($('who').hidden) {
    drawChoices();
    for (const button of $('choices').children) {
      button.disabled = true;
      button.classList.toggle('right', button.textContent === puzzle.label);
    }
  }
  drawGrid();
  drawStreak();
  drawPills();
  const n = record.tries.length;
  $('end').hidden = false;
  $('verdict').textContent = record.won
    ? `Solved in ${n} ${n === 1 ? 'try' : 'tries'}.`
    : 'Not this time.';
  $('reveal').innerHTML = `<b>${escapeHtml(puzzle.title)}</b> — ${escapeHtml(puzzle.label)}.`
    + (puzzle.lead ? ` ${escapeHtml(puzzle.lead)}.` : '');
  const days = Object.values(results).filter((r) => r?.done);
  const won = days.filter((r) => r.won).length;
  $('stats').textContent = `Played ${days.length} · won ${days.length ? Math.round((100 * won) / days.length) : 0}%`
    + ` · streak ${streakOf(results, today)}`;
  $('watch').href = `../watch/player.html?game=${encodeURIComponent(puzzle.id)}&ply=${puzzle.start}`;
  $('guess').href = `../guess/?game=${encodeURIComponent(puzzle.id)}`;
  $('next').textContent = 'A new game at midnight.';
  table.setLead(record.won ? 'Solved.' : 'The answer is on the board.');
  table.setTag('');
}

async function share() {
  const text = shareText({
    day: today,
    tries: record.tries,
    won: record.won,
    who: record.who,
    streak: streakOf(results, today),
    url: publicUrl(`${location.origin}${location.pathname}`),
  });
  let how = 'copy';
  try {
    if (typeof navigator.share === 'function') {
      await navigator.share({ text });
      how = 'native';
    } else {
      await navigator.clipboard.writeText(text);
      $('share-note').textContent = 'Copied — paste it anywhere.';
    }
  } catch {
    $('share-note').textContent = text;
  }
  count('share-card', how);
  return text;
}

// ------------------------------------------------------------------- wiring

$('undo').addEventListener('click', undo);
$('give-up').addEventListener('click', () => {
  if (phase === 'guessing' && !busy) reveal(false);
});
$('choices').addEventListener('click', (event) => {
  const button = event.target.closest('button[data-at]');
  if (button) choose(puzzle.choices[Number(button.dataset.at)]);
});
$('share').addEventListener('click', share);
addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey || event.target.matches('input, textarea')) return;
  const key = event.key.toLowerCase();
  if (key === 'u') { event.preventDefault(); undo(); }
  if (key === 's' && phase === 'guessing') { event.preventDefault(); reveal(false); }
});

// ------------------------------------------------------------------- and go

drawAsk();
drawStreak();
table.state = 'playing';
table.explainMate(false);
await toStart();
await table.faceYourSide(true);
count('daily-open', `d${today}`);

if (record.done) {
  await finish();
} else if (record.revealed || record.tries.length >= TRIES || record.tries.some(solved)) {
  // Tried to the end (or gave up) before a reload, but the question was never answered.
  await reveal(record.tries.some(solved));
} else {
  drawGrid();
  lead();
}

// What `scripts/check-daily.mjs` drives the page through.
window.__daily = {
  puzzle,
  get phase() { return phase; },
  get busy() { return busy; },
  get record() { return JSON.parse(JSON.stringify(record)); },
  get current() { return [...current]; },
  /** Every legal move where the board is now, in UCI — so a check can play a wrong line. */
  legal: () => live.legal().map(uciOf),
  play: (uci) => {
    const mv = live.fromUci(uci);
    if (!mv) throw new Error(`${uci} is not legal here`);
    return table.onPick(mv);
  },
  choose,
  giveUp: () => reveal(false),
  undo,
  share,
};
